import { useRef, useState, useEffect } from "react";
import { AlertTriangle, ShieldAlert } from "lucide-react";
import { useMovingWatermark, useKeyboardBlock, useVisibilityPause } from "@/hooks/useAntiPiracy";

export default function ProtectedVideoPlayer({
  videoUrl,
  videoTitle,
  videoKey,
  isYouTube,
  youTubeId,
  gDriveEmbed,
  shouldProtect,
  user,
  courseId,
  onPlay,
  onPause,
  onEnded,
  onTimeUpdate,
  onError,
  videoError,
  setVideoError,
  setVideoKey,
}) {
  const videoRef = useRef(null);
  const { pos, watermarkText } = useMovingWatermark(user, courseId);
  const { showWarning, setShowWarning } = useVisibilityPause(shouldProtect, videoRef);
  useKeyboardBlock(shouldProtect);

  const preventContextMenu = (e) => e.preventDefault();

  // Screen capture detection CSS
  const protectedStyle = shouldProtect ? {
    WebkitUserSelect: "none",
    userSelect: "none",
    WebkitTouchCallout: "none",
  } : {};

  return (
    <div
      className="relative bg-black aspect-video w-full"
      data-testid="video-player-container"
      onContextMenu={shouldProtect ? preventContextMenu : undefined}
      style={protectedStyle}
    >
      {/* Video content */}
      {videoUrl ? (
        gDriveEmbed && !isYouTube ? (
          <iframe src={gDriveEmbed} title={videoTitle} className="w-full h-full" allow="autoplay" allowFullScreen frameBorder="0" />
        ) : isYouTube ? (
          <iframe
            src={`https://www.youtube.com/embed/${youTubeId}?autoplay=1&rel=0&modestbranding=1${shouldProtect ? "&disablekb=1" : ""}`}
            title={videoTitle}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope"
            allowFullScreen
            frameBorder="0"
          />
        ) : (
          <>
            <video
              key={videoKey}
              ref={videoRef}
              className="w-full h-full"
              controls
              controlsList={shouldProtect ? "nodownload noremoteplayback noplaybackrate" : "nodownload noremoteplayback"}
              disablePictureInPicture={shouldProtect}
              onContextMenu={preventContextMenu}
              playsInline
              preload="auto"
              onPlay={onPlay}
              onPause={onPause}
              onEnded={onEnded}
              onTimeUpdate={onTimeUpdate}
              onError={onError}
            >
              <source src={videoUrl} type={videoUrl?.endsWith(".webm") ? "video/webm" : "video/mp4"} />
            </video>
            {videoError && (
              <div className="absolute inset-0 bg-black/90 flex items-center justify-center p-6">
                <div className="text-center">
                  <AlertTriangle size={24} className="text-red-400 mx-auto mb-3" />
                  <p className="text-sm text-red-400 font-bold mb-1">Video Error</p>
                  <p className="text-xs text-slate-400 mb-3">{videoError}</p>
                  <button onClick={() => { setVideoError(""); setVideoKey(k => k + 1); }} className="bg-[#0284C7] text-white text-xs font-bold px-4 py-2">Retry</button>
                </div>
              </div>
            )}
          </>
        )
      ) : (
        <div className="w-full h-full flex items-center justify-center text-slate-500">
          <div className="text-center">
            <div className="w-10 h-10 mx-auto mb-2 opacity-30 border-2 border-slate-500 rounded-full flex items-center justify-center">
              <div className="w-0 h-0 border-t-[8px] border-t-transparent border-b-[8px] border-b-transparent border-l-[14px] border-l-slate-500 ml-1" />
            </div>
            <p className="text-sm">Select a video to start watching</p>
          </div>
        </div>
      )}

      {/* Dynamic Moving Watermark (Students only) */}
      {shouldProtect && videoUrl && (
        <div
          className="absolute pointer-events-none z-20 transition-all duration-1000 ease-in-out"
          style={{ top: `${pos.top}%`, left: `${pos.left}%`, maxWidth: "45%" }}
          data-testid="video-watermark"
        >
          <p className="text-[10px] sm:text-xs text-white/30 font-mono whitespace-nowrap select-none" style={{ textShadow: "0 0 4px rgba(0,0,0,0.5)" }}>
            {watermarkText}
          </p>
        </div>
      )}

      {/* Screen Recording Warning */}
      {showWarning && (
        <div className="absolute inset-0 bg-black/95 flex items-center justify-center z-30 p-6" data-testid="recording-warning">
          <div className="text-center">
            <ShieldAlert size={48} className="text-red-500 mx-auto mb-4" />
            <h3 className="text-white text-lg font-bold mb-2">Screen Recording Detected</h3>
            <p className="text-slate-400 text-sm mb-4">Video has been paused. Screen recording or switching apps during playback is not allowed.</p>
            <button
              onClick={() => { setShowWarning(false); videoRef.current?.play(); }}
              className="bg-[#0284C7] text-white text-sm font-bold px-6 py-2.5 rounded-lg"
              data-testid="resume-playback-btn"
            >
              Resume Playback
            </button>
          </div>
        </div>
      )}

      {/* Anti-capture overlay for screenshots (CSS trick) */}
      {shouldProtect && videoUrl && (
        <div className="absolute inset-0 pointer-events-none z-10" style={{ mixBlendMode: "difference", background: "transparent" }} />
      )}
    </div>
  );
}
